<script setup>
import { computed, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  agents: {
    type: Array,
    default: () => [],
  },
  teams: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const defaults = {
  pool: 'inbox',
  availability: 'online_only',
  assignment_behavior: 'if_unassigned',
  team_id: null,
  agent_ids: [],
};

const config = computed(() => ({
  ...defaults,
  ...(props.modelValue[0] || {}),
}));

const updateConfig = attributes => {
  emit('update:modelValue', [{ ...config.value, ...attributes }]);
};

watch(
  () => props.modelValue,
  value => {
    if (!value.length) emit('update:modelValue', [{ ...defaults }]);
  },
  { immediate: true }
);

const pool = computed({
  get: () => config.value.pool,
  set: value => updateConfig({ pool: value, team_id: null, agent_ids: [] }),
});

const availability = computed({
  get: () => config.value.availability,
  set: value => updateConfig({ availability: value }),
});

const assignmentBehavior = computed({
  get: () => config.value.assignment_behavior,
  set: value => updateConfig({ assignment_behavior: value }),
});

const teamId = computed({
  get: () => config.value.team_id,
  set: value => updateConfig({ team_id: value }),
});

const selectedAgents = computed({
  get: () => {
    const selectedIds = new Set(config.value.agent_ids.map(Number));
    return props.agents.filter(agent => selectedIds.has(Number(agent.id)));
  },
  set: value => updateConfig({ agent_ids: value.map(agent => agent.id) }),
});
</script>

<template>
  <div class="mt-2 grid gap-3 rounded-lg bg-n-alpha-2 p-3 md:grid-cols-3">
    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.POOL') }}
      <select v-model="pool" class="mb-0 w-full text-sm">
        <option value="inbox">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.POOL_INBOX') }}
        </option>
        <option value="current_team">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.POOL_CURRENT_TEAM') }}
        </option>
        <option value="team">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.POOL_TEAM') }}
        </option>
        <option value="agents">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.POOL_AGENTS') }}
        </option>
      </select>
    </label>

    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.AVAILABILITY') }}
      <select v-model="availability" class="mb-0 w-full text-sm">
        <option value="online_only">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.ONLINE_ONLY') }}
        </option>
        <option value="online_preferred">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.ONLINE_PREFERRED') }}
        </option>
        <option value="any">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.ANY_AVAILABILITY') }}
        </option>
      </select>
    </label>

    <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.REASSIGNMENT') }}
      <select v-model="assignmentBehavior" class="mb-0 w-full text-sm">
        <option value="if_unassigned">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.IF_UNASSIGNED') }}
        </option>
        <option value="outside_pool">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.OUTSIDE_POOL') }}
        </option>
        <option value="always">
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.ALWAYS') }}
        </option>
      </select>
    </label>

    <label
      v-if="pool === 'team'"
      class="flex flex-col gap-1 text-xs font-medium text-n-slate-11 md:col-span-3"
    >
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.TEAM') }}
      <select v-model="teamId" class="mb-0 w-full text-sm">
        <option :value="null" disabled>
          {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.SELECT_TEAM') }}
        </option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
    </label>

    <label
      v-if="pool === 'agents'"
      class="flex flex-col gap-1 text-xs font-medium text-n-slate-11 md:col-span-3"
    >
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.AGENTS') }}
      <multiselect
        v-model="selectedAgents"
        track-by="id"
        label="name"
        multiple
        selected-label
        deselect-label=""
        :options="agents"
        :allow-empty="false"
        :placeholder="$t('AUTOMATION.ACTION.AUTO_ASSIGN.SELECT_AGENTS')"
      />
    </label>

    <p class="m-0 text-xs text-n-slate-10 md:col-span-3">
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.ELIGIBILITY_NOTE') }}
    </p>
    <p class="m-0 text-xs text-n-slate-10 md:col-span-3">
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.NO_CANDIDATE_NOTE') }}
    </p>
    <p class="m-0 text-xs text-n-amber-11 md:col-span-3">
      {{ $t('AUTOMATION.ACTION.AUTO_ASSIGN.MODE_NOTE') }}
    </p>
  </div>
</template>
